import { SaveManager } from '../state/SaveManager.js';
import {
  soundscapeRegistry,
  type SoundAssetDefinition,
  type SoundAssetKind,
  type WorldSoundscapeDefinition,
} from './SoundscapeRegistry.js';

export interface AudioPreferences {
  readonly muted: boolean;
  readonly masterVolume: number;
  readonly musicVolume: number;
  readonly ambienceVolume: number;
  readonly sfxVolume: number;
}

export type AudioPauseReason = 'scene' | 'background' | 'user';

interface ManagedLoop {
  readonly kind: 'music' | 'ambience';
  readonly element: HTMLAudioElement;
  readonly gain: number;
  fade: number;
  fadeTimer?: number;
}

const clampVolume = (value: number | undefined, fallback: number) => {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.min(1, value));
};

/**
 * Canonical owner for procedural feedback, registered one-shot files, and
 * declarative world music/ambience. Missing optional registrations resolve to
 * silence and never cause network requests.
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private preferences: AudioPreferences;
  private readonly pauseReasons = new Set<AudioPauseReason>();
  private activeSoundscapeId?: string;
  private activeMusic?: ManagedLoop;
  private readonly activeAmbience = new Map<string, ManagedLoop>();
  private readonly managedLoops = new Set<ManagedLoop>();
  private readonly oneShots = new Set<HTMLAudioElement>();
  private transitionId = 0;

  constructor() {
    const settings = SaveManager.getSettings();
    this.preferences = {
      muted: settings.muted,
      masterVolume: clampVolume(settings.masterVolume, 1),
      musicVolume: clampVolume(settings.musicVolume, 0.7),
      ambienceVolume: clampVolume(settings.ambienceVolume, 0.65),
      sfxVolume: clampVolume(settings.sfxVolume, 1),
    };
  }

  private getCtx(): AudioContext {
    if (!this.ctx) {
      const AudioContextConstructor = window.AudioContext
        || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AudioContextConstructor();
    }
    return this.ctx;
  }

  private busVolume(kind: SoundAssetKind) {
    if (kind === 'music') return this.preferences.musicVolume;
    if (kind === 'ambience') return this.preferences.ambienceVolume;
    return this.preferences.sfxVolume;
  }

  private effectiveVolume(kind: SoundAssetKind, gain: number, fade = 1) {
    if (this.preferences.muted) return 0;
    return clampVolume(
      this.preferences.masterVolume * this.busVolume(kind) * gain * fade,
      0,
    );
  }

  private tone(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    volume = 0.25,
    delay = 0,
  ) {
    if (this.preferences.muted || this.pauseReasons.size > 0) return;
    try {
      const ctx = this.getCtx();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime + delay);
      gain.gain.setValueAtTime(
        Math.max(0.001, this.effectiveVolume('sfx', volume)),
        ctx.currentTime + delay,
      );
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + duration);
      oscillator.start(ctx.currentTime + delay);
      oscillator.stop(ctx.currentTime + delay + duration);
    } catch { /* Browser audio can remain silent until a user gesture. */ }
  }

  registerTrack(definition: SoundAssetDefinition) {
    soundscapeRegistry.registerAsset(definition);
  }

  registerSoundscape(definition: WorldSoundscapeDefinition) {
    soundscapeRegistry.registerSoundscape(definition);
  }

  async enterSoundscape(definitionOrId: WorldSoundscapeDefinition | string) {
    if (typeof definitionOrId !== 'string') this.registerSoundscape(definitionOrId);
    const id = typeof definitionOrId === 'string' ? definitionOrId : definitionOrId.id;
    const definition = soundscapeRegistry.getSoundscape(id);
    if (!definition || this.activeSoundscapeId === id) return;

    const transitionId = ++this.transitionId;
    const fadeOutMs = definition.fadeOutMs ?? 450;
    const fadeInMs = definition.fadeInMs ?? 650;
    const oldMusic = this.activeMusic;
    const oldAmbience = [...this.activeAmbience.values()];
    this.activeMusic = undefined;
    this.activeAmbience.clear();
    this.activeSoundscapeId = id;

    if (oldMusic) this.fadeLoop(oldMusic, 0, fadeOutMs, true);
    oldAmbience.forEach(loop => this.fadeLoop(loop, 0, fadeOutMs, true));

    if (definition.musicKey) {
      this.activeMusic = this.createLoop(
        definition.musicKey,
        'music',
        definition.musicVolume ?? 1,
        fadeInMs,
      );
    }
    definition.ambienceKeys?.forEach(key => {
      const loop = this.createLoop(
        key,
        'ambience',
        definition.ambienceVolume ?? 1,
        fadeInMs,
      );
      if (loop) this.activeAmbience.set(key, loop);
    });

    if (transitionId !== this.transitionId) return;
    await this.resumeActiveLoops();
  }

  leaveSoundscape(options: { id?: string; fadeMs?: number } = {}) {
    if (options.id && options.id !== this.activeSoundscapeId) return;
    ++this.transitionId;
    this.activeSoundscapeId = undefined;
    const fadeMs = options.fadeMs ?? 400;
    if (this.activeMusic) this.fadeLoop(this.activeMusic, 0, fadeMs, true);
    this.activeMusic = undefined;
    this.activeAmbience.forEach(loop => this.fadeLoop(loop, 0, fadeMs, true));
    this.activeAmbience.clear();
  }

  private createLoop(
    key: string,
    kind: 'music' | 'ambience',
    soundscapeGain: number,
    fadeInMs: number,
  ) {
    const definition = soundscapeRegistry.getAsset(key);
    if (!definition || definition.kind !== kind) return undefined;

    const element = new Audio(definition.path);
    element.loop = true;
    element.preload = 'auto';
    const loop: ManagedLoop = {
      kind,
      element,
      gain: clampVolume(definition.volume, 1) * clampVolume(soundscapeGain, 1),
      fade: fadeInMs > 0 ? 0 : 1,
    };
    this.managedLoops.add(loop);
    this.applyLoopVolume(loop);
    if (fadeInMs > 0) this.fadeLoop(loop, 1, fadeInMs, false);
    return loop;
  }

  private applyLoopVolume(loop: ManagedLoop) {
    loop.element.volume = this.effectiveVolume(loop.kind, loop.gain, loop.fade);
  }

  private fadeLoop(loop: ManagedLoop, target: number, durationMs: number, stopAtEnd: boolean) {
    if (loop.fadeTimer !== undefined) window.clearInterval(loop.fadeTimer);
    if (durationMs <= 0) {
      loop.fade = target;
      this.applyLoopVolume(loop);
      if (stopAtEnd) this.disposeLoop(loop);
      return;
    }

    const startFade = loop.fade;
    const startTime = performance.now();
    loop.fadeTimer = window.setInterval(() => {
      const progress = Math.min(1, (performance.now() - startTime) / durationMs);
      loop.fade = startFade + (target - startFade) * progress;
      this.applyLoopVolume(loop);
      if (progress < 1) return;
      if (loop.fadeTimer !== undefined) window.clearInterval(loop.fadeTimer);
      loop.fadeTimer = undefined;
      if (stopAtEnd) this.disposeLoop(loop);
    }, 32);
  }

  private disposeLoop(loop: ManagedLoop) {
    if (loop.fadeTimer !== undefined) window.clearInterval(loop.fadeTimer);
    loop.element.pause();
    loop.element.removeAttribute('src');
    loop.element.load();
    this.managedLoops.delete(loop);
  }

  private async resumeActiveLoops() {
    if (this.pauseReasons.size > 0 || this.preferences.muted) return;
    const loops = [this.activeMusic, ...this.activeAmbience.values()].filter(
      (loop): loop is ManagedLoop => loop !== undefined,
    );
    await Promise.all(loops.map(loop => loop.element.play().catch(() => undefined)));
  }

  playSfx(key: string) {
    const definition = soundscapeRegistry.getAsset(key);
    if (!definition || definition.kind !== 'sfx'
      || this.preferences.muted || this.pauseReasons.size > 0) return;
    const element = new Audio(definition.path);
    element.preload = 'auto';
    element.volume = this.effectiveVolume('sfx', clampVolume(definition.volume, 1));
    const cleanup = () => {
      element.removeEventListener('ended', cleanup);
      element.removeEventListener('error', cleanup);
      this.oneShots.delete(element);
    };
    element.addEventListener('ended', cleanup);
    element.addEventListener('error', cleanup);
    this.oneShots.add(element);
    void element.play().catch(cleanup);
  }

  pause(reason: AudioPauseReason) {
    this.pauseReasons.add(reason);
    this.managedLoops.forEach(loop => loop.element.pause());
    this.oneShots.forEach(element => element.pause());
    if (this.ctx?.state === 'running') void this.ctx.suspend().catch(() => undefined);
  }

  async resume(reason: AudioPauseReason) {
    this.pauseReasons.delete(reason);
    if (this.pauseReasons.size > 0 || this.preferences.muted) return;
    if (this.ctx?.state === 'suspended') await this.ctx.resume().catch(() => undefined);
    await this.resumeActiveLoops();
  }

  async resumeFromGesture() {
    if (this.ctx?.state === 'suspended') await this.ctx.resume().catch(() => undefined);
    await this.resumeActiveLoops();
  }

  setPreferences(preferences: Partial<AudioPreferences>) {
    this.preferences = {
      muted: preferences.muted ?? this.preferences.muted,
      masterVolume: clampVolume(preferences.masterVolume, this.preferences.masterVolume),
      musicVolume: clampVolume(preferences.musicVolume, this.preferences.musicVolume),
      ambienceVolume: clampVolume(preferences.ambienceVolume, this.preferences.ambienceVolume),
      sfxVolume: clampVolume(preferences.sfxVolume, this.preferences.sfxVolume),
    };
    SaveManager.updateSettings(this.preferences);
    this.managedLoops.forEach(loop => this.applyLoopVolume(loop));
    if (this.preferences.muted) {
      this.managedLoops.forEach(loop => loop.element.pause());
      this.oneShots.forEach(element => element.pause());
    } else {
      void this.resumeActiveLoops();
    }
  }

  getPreferences(): AudioPreferences { return { ...this.preferences }; }
  setMuted(muted: boolean) { this.setPreferences({ muted }); }
  isMuted() { return this.preferences.muted; }

  click() { this.tone(880, 0.06, 'square', 0.15); }

  correct() {
    this.tone(523.25, 0.12);
    this.tone(659.25, 0.12, 'sine', 0.25, 0.1);
    this.tone(783.99, 0.2, 'sine', 0.25, 0.2);
  }

  wrong() {
    this.tone(330, 0.15, 'sawtooth', 0.2);
    this.tone(220, 0.25, 'sawtooth', 0.2, 0.15);
  }

  hint() {
    this.tone(440, 0.1, 'sine', 0.15);
    this.tone(523, 0.15, 'sine', 0.15, 0.12);
  }

  bridgeSegment() {
    [523.25, 659.25, 783.99, 1046.5]
      .forEach((frequency, index) => this.tone(frequency, 0.18, 'sine', 0.3, index * 0.08));
  }

  portalActivate() {
    for (let index = 0; index < 8; index++) {
      this.tone(200 + index * 80, 0.15, 'sine', 0.12, index * 0.06);
    }
  }

  celebrate() {
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5];
    notes.forEach((frequency, index) => this.tone(frequency, 0.22, 'sine', 0.3, index * 0.09));
    notes.forEach((frequency, index) => this.tone(
      frequency * 2, 0.15, 'sine', 0.15, 0.5 + index * 0.07,
    ));
  }

  restoration() {
    const melody = [523.25, 587.33, 659.25, 698.46, 783.99, 880, 987.77, 1046.5];
    melody.forEach((frequency, index) => this.tone(
      frequency, 0.3, 'sine', 0.35, index * 0.12,
    ));
    melody.forEach((frequency, index) => this.tone(
      frequency * 1.5, 0.25, 'triangle', 0.2, 1 + index * 0.1,
    ));
  }

  sparkle() { this.tone(1200 + Math.random() * 800, 0.08, 'sine', 0.1); }

  unlock() {
    [392, 523.25, 659.25, 880]
      .forEach((frequency, index) => this.tone(frequency, 0.2, 'sine', 0.28, index * 0.1));
  }

  levelUp() {
    [261.63, 329.63, 392, 523.25, 659.25, 783.99]
      .forEach((frequency, index) => this.tone(frequency, 0.25, 'sine', 0.32, index * 0.1));
  }

  destroy() {
    ++this.transitionId;
    [...this.managedLoops].forEach(loop => this.disposeLoop(loop));
    this.oneShots.forEach(element => {
      element.pause();
      element.removeAttribute('src');
    });
    this.oneShots.clear();
    if (this.ctx) void this.ctx.close().catch(() => undefined);
    this.ctx = null;
  }
}

export const audio = new AudioManager();
